import React, { useState, useEffect, useMemo } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { French } from "flatpickr/dist/l10n/fr.js";
import { Editor } from "@tinymce/tinymce-react";
import { cleanHtml, replaceSmartQuotes } from "../../utils/procurementForm.utils";

export default function ProcurementMarketInfoSection({
  montantAffiche,
  setValue,
  errors,
  watch,
  values,
  onCleanedMarketInfoChange,
}) {
  const [cleanedFields, setCleanedFields] = useState({
    context: "",
    object: "",
  });

  const [montantLocal, setMontantLocal] = useState(montantAffiche ?? "");
  const [inputAmount, setInputAmount] = useState("");
  const [syncedOnce, setSyncedOnce] = useState(false);

  function sanitizeEuroInput(str) {
    if (typeof str !== "string") return "";
    let s = str.replace(/\s|[\u00A0\u202F]/g, "");
    if (s.includes(".") && !s.includes(",")) {
      s = s.replace(/\./g, ",");
    }
    s = s.replace(/[^\d,]/g, "");
    const firstComma = s.indexOf(",");
    if (firstComma !== -1) {
      const head = s.slice(0, firstComma + 1);
      const tail = s.slice(firstComma + 1).replace(/,/g, "");
      s = head + tail;
    }
    const parts = s.split(",");
    if (parts.length > 1) {
      parts[1] = parts[1].slice(0, 2);
      s = parts[0] + "," + parts[1];
    }
    return s;
  }

  function parseEuroWithComma(str) {
    if (!str && str !== 0) return NaN;

    let s = String(str).replace(/\s|[\u00A0\u202F]/g, "");
    const hasComma = s.includes(",");
    const hasDot = s.includes(".");

    if (hasComma && hasDot) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else if (hasComma) {
      s = s.replace(",", ".");
    } else if (hasDot) {
      const first = s.indexOf(".");
      s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, "");
    }

    const n = Number(s);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
  }

  function formatNumberFr(value) {
    if (!Number.isFinite(value)) return "";
    const hasNonZeroCents = Math.round((value * 100) % 100) !== 0;
    const opts = hasNonZeroCents
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return new Intl.NumberFormat("fr-BE", opts).format(value);
  }

  function formatForInput(value) {
    return formatNumberFr(value);
  }

  /**
   * -----------------------------
   * TinyMCE (bloc contexte)
   * -----------------------------
   */
  const CONTEXT_LOCK_ID = "context-intro-locked-block";
  const CONTEXT_PREFIX_HTML = `
    <div id="${CONTEXT_LOCK_ID}" class="mceNonEditable context-prefix" data-locked="1" contenteditable="false" style="padding:12px;border:1px dashed #9ca3af;border-radius:6px;background:#f9fafb;margin-bottom:12px;">
      <p style="margin:0 0 6px 0;">
        <strong>
          ⚠️ Le texte ci-dessous apparaîtra toujours en tête du contexte. Veuillez rédiger votre contenu à la suite.
        </strong>
      </p>
      <div>
        L'Agence du Numérique (AdN) est une SA de droit public, dont le décret régional du 19 octobre 2022 est la base légale.
        Elle est chargée du développement du Numérique en Wallonie.<br />
        De manière plus précise, l'Agence du Numérique est le service public chargé d'assurer une veille sur les innovations
        technologiques et les usages du numérique, de conseiller le Gouvernement wallon et ses services dans ce domaine, et
        de mener ou de coordonner les actions opérationnelles ou de communication visant à la transformation numérique de
        la Wallonie en s'appuyant sur la stratégie numérique Digital Wallonia.
      </div>
    </div>`.trim();

  useEffect(() => {
    if (typeof onCleanedMarketInfoChange === "function") {
      onCleanedMarketInfoChange(cleanedFields);
    }
  }, [cleanedFields, onCleanedMarketInfoChange]);

  useEffect(() => {
    const isEditing = document.activeElement && document.activeElement.tagName === "INPUT";
    if (!isEditing) {
      setMontantLocal(montantAffiche ?? "");
    }
  }, [montantAffiche]);

  useEffect(() => {
    if (syncedOnce) return;

    const fromProp = (montantAffiche ?? "").trim();
    if (fromProp) {
      setInputAmount(sanitizeEuroInput(fromProp));
      setSyncedOnce(true);
      return;
    }

    const v = values.amountVAT ?? watch("amountVAT");
    if (v != null && v !== "") {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) {
        const s = n % 1 === 0 ? String(Math.trunc(n)) : n.toFixed(2);
        setInputAmount(s.replace(".", ","));
        setSyncedOnce(true);
      }
    }
  }, [syncedOnce, montantAffiche, values.amountVAT, watch]);

  function getString(val) {
    return typeof val === "string" ? val : "";
  }

  function handleEditorChange(field, newContent) {
    setValue(field, newContent, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setCleanedFields(prev => ({
      ...prev,
      [field]: cleanHtml(newContent),
    }));
  }

  const [durationCount, setDurationCount] = useState("");
  const [durationUnit, setDurationUnit] = useState("");

  const tvaFactor = 1.21;
  const seuilHtva = 30000;
  const MAX_HTVA = 140000;
  const TVA_RATE = 0.21;
  const MAX_TVAC = Math.round(MAX_HTVA * (1 + TVA_RATE));

  const amountVAT = values.amountVAT;
  const montantHTVA = amountVAT ? Number(amountVAT) / tvaFactor : 0;

  const fundingSources = [
    { value: "Dotation", label: "Dotation" },
    { value: "Dotation de fonctionnement", label: "Dotation de fonctionnement" },
    { value: "DW 34", label: "DW 34" },
    { value: "PNRR 134", label: "PNRR 134" },
    { value: "PRRR 133", label: "PRRR 133" },
    { value: "PRW 130", label: "PRW 130" },
    { value: "PRW 131", label: "PRW 131" },
    { value: "PRW 134", label: "PRW 134" },
    { value: "PRW 136", label: "PRW 136" },
    { value: "PRW 137", label: "PRW 137" },
    { value: "PRW 138", label: "PRW 138" },
    { value: "PRW 139", label: "PRW 139" },
    { value: "PRW 140", label: "PRW 140" },
    { value: "PRW 225", label: "PRW 225" },
  ];

  function handleEditorPaste(e) {
    let html = e.content;
    html = replaceSmartQuotes(html);
    e.content = html;
  }

  function configureContextEditor(editor, { lockId, prefixHtml }) {
    function isInLocked(node) {
      if (!node) return false;
      const el = node.nodeType === 1 ? node : node.parentNode;
      return !!(el && (el.id === lockId || (el.closest && el.closest(`#${lockId}`))));
    }

    function makeLockedFirst() {
      const doc = editor.getDoc();
      if (!doc) return;
      const body = doc.body;
      const locked = doc.getElementById(lockId);
      if (!body || !locked) return;
      while (body.firstChild && body.firstChild !== locked) {
        body.insertBefore(body.firstChild, locked.nextSibling);
      }
    }

    function ensurePrefix() {
      const doc = editor.getDoc();
      if (!doc) return;

      let exists = doc.getElementById(lockId);
      if (!exists) {
        const current = editor.getContent({ format: "html" }) || "";
        editor.setContent(prefixHtml + current);
        setTimeout(() => {
          makeLockedFirst();
          placeCaretAfterPrefix();
        }, 0);
        return;
      }

      exists.setAttribute("contenteditable", "false");
      if (!exists.classList.contains("mceNonEditable")) {
        exists.classList.add("mceNonEditable");
      }
      makeLockedFirst();
      if (!exists.nextSibling) {
        editor.dom.insertAfter(editor.dom.create("p", {}, "<br>"), exists);
      }
    }
    function caretIsBeforeLocked() {
      const doc = editor.getDoc();
      if (!doc) return false;
      const locked = doc.getElementById(lockId);
      if (!locked) return false;

      const sel = editor.selection;
      const rng = sel.getRng();
      if (!rng) return false;

      let node = rng.startContainer;
      if (!node) return false;

      if (isInLocked(node)) return false;
      if (node === doc.body && rng.startOffset === 0) return true;
      const el = node.nodeType === 1 ? node : node.parentNode;
      if (!el || el === locked) return false;

      if (el.compareDocumentPosition) {
        return !!(el.compareDocumentPosition(locked) & Node.DOCUMENT_POSITION_FOLLOWING);
      }
      return false;
    }
    function placeCaretAfterPrefix() {
      const doc = editor.getDoc();
      if (!doc) return;

      const locked = doc.getElementById(lockId);
      if (!locked) return;

      let node = locked.nextSibling;
      if (!node) {
        editor.dom.insertAfter(editor.dom.create("p", {}, "<br>"), locked);
        node = locked.nextSibling;
      }

      try {
        const rng = doc.createRange();
        if (node.nodeType === 1 && node.firstChild) {
          rng.setStart(node.firstChild, 0);
        } else {
          rng.setStart(node, 0);
        }
        rng.collapse(true);
        editor.selection.setRng(rng);
      } catch {}
    }

    editor.on("PastePreProcess", (e) => {
      if (typeof e.content === "string") {
        e.content = replaceSmartQuotes(e.content);
      }
      ensurePrefix();
      if (caretIsBeforeLocked()) {
        placeCaretAfterPrefix();
      }
    });

    editor.on("keydown", (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        e.preventDefault();
        selectAllAfterLocked();
      }
    });
    editor.addCommand("SelectAll", selectAllAfterLocked);
    editor.on("mousedown", (e) => {
      const doc = editor.getDoc();
      if (!doc) return;
      const locked = doc.getElementById(lockId);
      if (!locked) return;
      const t = e.target;
      if (t === locked || (t.closest && t.closest(`#${lockId}`))) {
        e.preventDefault();
        placeCaretAfterPrefix();
      }
    });

    const enforceCaret = () => {
      ensurePrefix();

      const sel = editor.selection;
      if (!sel) return;

      const rng = sel.getRng();
      if (!rng) return;

      if (!rng.collapsed) return;

      if (caretIsBeforeLocked()) {
        placeCaretAfterPrefix();
      }
    };

    function selectAllAfterLocked() {
      ensurePrefix();

      const doc = editor.getDoc();
      if (!doc) return;

      const locked = doc.getElementById(lockId);
      if (!locked) {
        editor.execCommand("mceSelectAll");
        return;
      }

      let start = locked.nextSibling;
      if (!start) {
        const p = editor.dom.create("p", {}, "<br>");
        editor.dom.insertAfter(p, locked);
        start = p;
      }

      const rng = doc.createRange();
      if (start.nodeType === 1 && start.firstChild) {
        rng.setStart(start.firstChild, 0);
      } else {
        rng.setStart(start, 0);
      }

      const last = doc.body.lastChild;
      if (last) {
        rng.setEndAfter(last);
      } else {
        rng.setEnd(doc.body, doc.body.childNodes.length);
      }

      editor.selection.setRng(rng);
    }

    editor.on("input SetContent", ensurePrefix);
    editor.on("click mouseup keyup SelectionChange NodeChange focus", enforceCaret);

    editor.on("dragstart", (e) => {
      if (isInLocked(e.target)) {
        e.preventDefault();
      }
    });

    editor.on("drop", (e) => {
      const doc = editor.getDoc();
      if (!doc) return;
      const locked = doc.getElementById(lockId);
      if (!locked) return;

      const t = e.target;
      const droppingOnLocked = t && (t.id === lockId || (t.closest && t.closest(`#${lockId}`)));
      const droppingBefore
        = t === doc.body && doc.body.firstChild === locked;

      if (droppingOnLocked || droppingBefore) {
        e.preventDefault();
        placeCaretAfterPrefix();
      }
    });

    editor.on("init", () => {
      ensurePrefix();
      placeCaretAfterPrefix(false);
      setTimeout(() => {
        try {
          editor.getWin().blur();
          if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
          }
        } catch {}
      }, 50);
    });

    let preventFocusUntil = Date.now() + 1000;

    editor.on("focus", () => {
      if (Date.now() < preventFocusUntil) {
        setTimeout(() => {
          try {
            editor.getWin().blur();
            if (document.activeElement && document.activeElement.blur) {
              document.activeElement.blur();
            }
          } catch {}
        }, 0);
      }
    });

    return {
      ensurePrefix,
      placeCaretAfterPrefix,
    };
  }

  const countOptions = useMemo(() => {
    return Array.from({ length: 31 }, (_, i) => i + 1);
  }, []);

  const unitOptions = [
    { value: "jours", label: "jours" },
    { value: "semaines", label: "semaines" },
    { value: "mois", label: "mois" },
    { value: "années", label: "années" },
  ];

  function formatDuration(count, unit) {
    if (!count || !unit) return "";
    const n = Number(count);

    if (unit === "mois") {
      return `${n} mois`;
    }

    if (unit === "années") {
      if (n === 1) return "1 an";
      return `${n} années`;
    }

    if (unit === "jours") {
      return n === 1 ? "1 jour" : `${n} jours`;
    }

    if (unit === "semaines") {
      return n === 1 ? "1 semaine" : `${n} semaines`;
    }

    return `${n} ${unit}`;
  }

  useEffect(() => {
    const source = getString(values.duration || watch("duration")).trim();

    if (!source) return;
    if (durationCount !== "" || durationUnit !== "") return;

    const normalized = source
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase();

    const m = normalized.match(
      /^(\d+)\s*(jour|jours|semaine|semaines|mois|an|année|années)$/,
    );
    if (!m) return;

    const n = m[1];
    const u = m[2];
    const normalizedUnit
      = u === "mois"
        ? "mois"
        : u === "an" || u === "année" || u === "années"
        ? "années"
        : u === "jour" || u === "jours"
        ? "jours"
        : "semaines";

    setDurationCount(n);
    setDurationUnit(normalizedUnit);
  // eslint-disable-next-line
  }, [values.duration]);

  useEffect(() => {
    if (durationCount === "" || durationUnit === "") return;

    const reconciled = formatDuration(durationCount, durationUnit);
    const current = getString(values.duration).trim();

    if (reconciled && reconciled !== current) {
      setValue("duration", reconciled, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  // eslint-disable-next-line
  }, [durationCount, durationUnit]);

  useEffect(() => {
    const reconciled = formatDuration(durationCount, durationUnit);
    if (reconciled) {
      setValue("duration", reconciled, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue("duration", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [durationCount, durationUnit, setValue]);

  const durationPreview = useMemo(() => {
    return formatDuration(durationCount, durationUnit);
  }, [durationCount, durationUnit]);

  return (
    <fieldset className="border border-gray-300 p-6 rounded">
      <legend className="block text-lg font-semibold bg-blue-100 px-4 py-2 leading-[1.6] rounded">
        Renseignements concernant le marché
      </legend>

      <label className="block mt-0">
        <span className="text-sm font-medium">Nom du marché</span>{" "}
        <span className="text-red-500">*</span>
        <input
          type="text"
          placeholder="Nom du marché"
          value={values.name || ""}
          maxLength={50}
          onInput={(e) => {
            if (e.target.value.length > 50) {
              e.target.value = e.target.value.slice(0, 50);
            }
          }}
          onChange={e =>
            setValue("name", e.target.value, {
              shouldValidate: true,
              shouldDirty: true,
            })}
          className={`mt-1 w-full border rounded px-3 py-2 ${errors.name ? "border-red-500" : "border-gray-300"}`}
        />

        <div className="flex justify-between text-sm mt-1">
          <span className="text-gray-500">
            Nom de marché parlant et de maximum 50 caractères sans caractères spéciaux (&,#,@,!,...)
          </span>
          <span
            className={`ml-2 ${
              (values.name || "").length >= 50
                ? "text-red-600 font-bold text-base"
                : "text-gray-400"
            }`}
          >
            {(values.name || "").length}/50
          </span>
        </div>
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">
            {errors.name.message}
          </p>
        )}

      </label>

      <div className="flex gap-4 items-start mt-4">
        {/* Montant TVAC */}
        <label className="flex-[1.2] block">
          <span className="text-sm font-medium">
            Montant TVAC du marché <span className="text-red-500">*</span>
          </span>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={inputAmount}
              onChange={(e) => {
                const cleaned = sanitizeEuroInput(e.target.value);
                const parsed  = parseEuroWithComma(cleaned);

                if (Number.isFinite(parsed) && parsed > MAX_TVAC) {
                  setInputAmount(String(MAX_TVAC));
                  setValue("amountVAT", MAX_TVAC, { shouldValidate: true, shouldDirty: true });
                  return;
                }

                setInputAmount(cleaned);
              }}

              onBlur={(e) => {
                const raw = (e.currentTarget.value || "").trim();
                if (!raw) {
                  setValue("amountVAT", null, { shouldValidate: true, shouldDirty: true });
                  setInputAmount("");
                  return;
                }

                const cleaned = sanitizeEuroInput(raw);
                const parsed  = parseEuroWithComma(cleaned);

                if (!Number.isFinite(parsed) || parsed <= 0) {
                  setValue("amountVAT", null, { shouldValidate: true, shouldDirty: true });
                  setInputAmount("");
                  return;
                }

                const bounded = Math.min(169400, Math.max(0, parsed));
                setValue("amountVAT", bounded, { shouldValidate: true, shouldDirty: true });
                setInputAmount(cleaned);
              }}

              className={`mt-1 w-full border rounded px-3 py-2 pr-10 ${errors.amountVAT ? "border-red-500" : "border-gray-300"}`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
              €
            </span>
          </div>
          {inputAmount && (
            <span className="text-xs text-gray-600 block pt-1">
              Montant HTVA : <strong>{formatNumberFr((Number(watch("amountVAT")) || 0) / 1.21)} €</strong>
            </span>
          )}
          <div className="min-h-[1.25rem] mt-1">
            {errors.amountVAT
              ? (
                <p className="text-red-500 text-sm">{errors.amountVAT.message}</p>
              )
              : (
                <span className="text-sm invisible">placeholder</span>
              )}
          </div>
        </label>

        {/* Type de marché */}
        <label className="flex-[1.8] block mx-6">
          <span className="text-sm font-medium">Type de marché</span>
          <div className="mt-1 w-full border rounded px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed">
            {Number(watch("amountVAT")) > 36300
              ? "Supérieur à 36 300 € (30 000 € HTVA)"
              : "Inférieur à 36 300 € (30 000 € HTVA)"}
          </div>
        </label>

        {/* Taux journalier */}
        <label className="flex-[1.2] block">
          <span className="text-sm font-medium leading-none">Taux journalier (TVAC)</span>
          <select
            value={values.dailyRate || ""}
            onChange={(e) => {
              const val = e.target.value === "" ? null : Number(e.target.value);
              setValue("dailyRate", val, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            className="mt-1 w-full border rounded px-3 py-2 bg-white text-gray-700"
          >
            <option value="">- Aucun -</option>
            <option value="650">786,5 € (650 €/HTVA)</option>
            <option value="880">1064,8 € (880 €/HTVA)</option>
          </select>
        </label>
      </div>

      <label className="block mt-4">
        <span className="text-sm font-medium">Contexte</span> {montantHTVA > seuilHtva && (
          <span className="text-red-500">*</span>
        )}
        <Editor
          apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
          value={getString(values.context)}
          onEditorChange={newContent => handleEditorChange("context", newContent)}
          init={{
            height: 380,
            menubar: false,
            plugins: "link visualblocks lists",
            toolbar:
              "bold italic underline strikethrough | link | bullist numlist | removeformat",
            toolbar_mode: "sliding",
            paste_as_text: false,
            paste_remove_styles: true,
            paste_webkit_styles: "",
            width: "100%",
            language: "fr_FR",
            statusbar: true,
            branding: false,
            elementpath: false,
            resize: true,
            placeholder: "objet du marché (coller du texte possible)",
            noneditable_class: "mceNonEditable",
            forced_root_block: "p",
            setup: (editor) => {
              configureContextEditor(editor, {
                lockId: CONTEXT_LOCK_ID,
                prefixHtml: CONTEXT_PREFIX_HTML,
              });
            },
            content_style: `
              .context-prefix { font-size: 0.95rem; }
            `,
            setupcontent_callback: undefined,
          }}
        />
        {errors.context && (
          <p className="text-red-500 text-sm mt-1">
            {errors.context.message}
          </p>
        )}
      </label>

      <label className="block mt-4">
        <span className="text-sm font-medium">Objet</span> <span className="text-red-500">*</span>
        <Editor
          apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
          value={getString(values.object)}
          onEditorChange={newContent => handleEditorChange("object", newContent)}
          init={{
            height: 380,
            menubar: false,
            plugins: "link visualblocks lists",
            toolbar: "bold italic underline strikethrough | link | bullist numlist | removeformat",
            toolbar_mode: "sliding",
            paste_as_text: false,
            paste_remove_styles: true,
            paste_webkit_styles: "",
            width: "100%",
            language: "fr_FR",
            statusbar: true,
            branding: false,
            elementpath: false,
            resize: true,
            placeholder: "objet du marché (coller du texte possible)",
            setup: (editor) => {
              editor.on("PastePreProcess", handleEditorPaste);
            },
          }}
        />
        {errors.object && (
          <p className="text-red-500 text-sm mt-1">
            {errors.object.message}
          </p>
        )}
      </label>

      {(!watch("amountVAT") || Number(watch("amountVAT")) <= 36300) && (
        <label className="block mt-4 mb-0">
          <span className="text-sm font-medium">
            Date et heure limite de remise des offres (heure locale Bruxelles) <span className="text-red-500">*</span>
          </span>
          <div className="relative mt-1">
            <Flatpickr
              data-enable-time
              value={values.offerLimitAt ? [values.offerLimitAt] : []}
              placeholder="jj-mm-yyyy  hh:mm"
              options={{
                enableTime: true,
                dateFormat: "d-m-Y H:i",
                time_24hr: true,
                locale: French,
              }}
              onClose={([date]) => {
                setValue("offerLimitAt", date, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={`w-full border rounded px-10 py-2 pr-10 ${errors.offerLimitAt
                ? "border-red-500"
                : "border-gray-300"
                }`}
              style={{ paddingLeft: "2.5rem" }}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            {values.offerLimitAt && (
              <button
                type="button"
                onClick={() => setValue("offerLimitAt", "")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          {errors.offerLimitAt && (
            <p className="text-red-500 text-sm mt-1">
              {errors.offerLimitAt.message}
            </p>
          )}
        </label>
      )}

      {/* Durée du marché + Aperçu */}
      <div className="mt-4">
        <div className="flex gap-4">
          <label className="w-2/3 block">
            <span className="text-sm font-medium">Durée du marché</span>{" "}
            <span className="text-red-500">*</span>
          </label>
          <label className="w-1/3 block">
            <span className="text-sm font-medium">Aperçu</span>{" "}
            <span className="text-red-500">*</span>
          </label>
        </div>

        <div className="flex gap-4 mt-1">
          <div className="w-2/3 grid grid-cols-2 gap-3">
            <select
              value={durationCount}
              onChange={e => setDurationCount(e.target.value)}
              className={`w-full border rounded px-3 py-2 ${errors.duration ? "border-red-500" : "border-gray-300"}`}
            >
              <option value="">Nombre</option>
              {countOptions.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <select
              value={durationUnit}
              onChange={e => setDurationUnit(e.target.value)}
              className={`w-full border rounded px-3 py-2 ${errors.duration ? "border-red-500" : "border-gray-300"}`}
            >
              <option value="">Unité</option>
              {unitOptions.map((u) => {
                const isOne = Number(durationCount) === 1;
                const singular
                  = u.value === "jours"
                    ? "jour"
                    : u.value === "semaines"
                    ? "semaine"
                    : u.value === "années"
                    ? "année"
                    : "mois";
                const label = isOne ? singular : u.label;
                return (
                  <option key={u.value} value={u.value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="w-1/3 flex items-center">
            <span
              className={`block w-full px-3 py-2 rounded mt-[4px] ${durationPreview
                  ? "text-gray-900 bg-gray-100"
                  : "text-gray-400 bg-gray-50"
                }`}
            >
              {durationPreview || "—"}
            </span>
          </div>
        </div>

        {errors.duration && (
          <p className="text-red-500 text-sm mt-1">
            {errors.duration.message}
          </p>
        )}
      </div>

      <div className="flex gap-4 items-start mt-4">
        <label className="w-1/2 block">
          <span className="text-sm font-medium">Date de fin du marché</span>{" "}
          <span className="text-red-500">*</span>
          <div className="relative mt-1">
            <Flatpickr
              value={values.endAt || ""}
              placeholder="jj-mm-yyyy"
              options={{
                enableTime: false,
                dateFormat: "d-m-Y",
                time_24hr: true,
                locale: French,
              }}
              onClose={([date]) => {
                date.setHours(date.getHours() + 10); // fix timezone
                setValue("endAt", date, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className="w-full border rounded py-2 pr-3 border-gray-300 h-[42px]"
              style={{ paddingLeft: "2.5rem" }}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            {values.endAt && (
              <button
                type="button"
                onClick={() => setValue("endAt", "")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          {errors.endAt && (
            <p className="text-red-500 text-sm mt-1">
              {errors.endAt.message}
            </p>
          )}
        </label>

        <label className="w-1/2 block">
          <span className="text-sm font-medium">Source de financement</span>{" "}
          <span className="text-red-500">*</span>
          <select
            value={values.fundingSource}
            onChange={e =>
              setValue("fundingSource", e.target.value, {
                shouldValidate: true,
                shouldDirty: true,
              })}
            className={`mt-1 w-full border rounded px-3 border-gray-300 h-[42px] mt-[7px] ${errors.fundingSource ? "border-red-500" : "border-gray-300"}`}
          >
            <option value="">Sélectionnez la source de financement</option>
            {fundingSources.map(src => (
              <option key={src.value} value={src.value}>
                {src.label}
              </option>
            ))}
          </select>
          {errors.fundingSource && (
            <p className="text-red-500 text-sm mt-1">
              {errors.fundingSource.message}
            </p>
          )}
        </label>
      </div>

    </fieldset>
  );
}
